import { Typography } from '@/components/atoms';
import { MapPinnedIcon } from '@/components/atoms/icons';
import { PlaceholderImage } from '@/components/atoms/PlaceholderImage';
import { ImageCarousel } from '@/components/molecules/ImageCarousel';
import { useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import type { ExploreVrBannerData } from '@/core/services/contentfulClient';
import { GuestUnitAboutAccordion } from '@/modules/guest-web/components/GuestUnitAboutAccordion';
import { useGuestLabels } from '@/modules/guest-web/hooks/useGuestLabels';
import {
  getGuestDetailsTypeScale,
  type GuestDetailsLayout,
} from '@/modules/guest-web/constants/guestLayout';
import type { GuestUnitDetailsModel } from '@/modules/guest-web/types/guest.types';
import React, { useMemo } from 'react';
import {
  Pressable,
  StyleSheet,
  View,
  type ImageSourcePropType,
} from 'react-native';
import { ExploreVrBanner } from '../components/ExploreVrBanner';
import { UnitDocumentsAccordion } from '../components/UnitDocumentsAccordion';
import type { AdhaListingLayout } from './adhaListingLayout';

export interface AdhaListingProjectPanelProps {
  readonly layout: AdhaListingLayout;
  /** Same shape as guest project details (`mapProjectToDetails`). */
  readonly project: GuestUnitDetailsModel;
  readonly images: readonly ImageSourcePropType[];
  /** Image list still resolving — show the carousel shimmer, not the placeholder. */
  readonly imagesLoading?: boolean;
  readonly onMapPress: () => void;
  readonly onDocumentPress: (documentId: string) => void;
  readonly downloadingDocumentId?: string | null;
  /** Contentful `exploreVrBanner` — omit/undefined hides the VR CTA. */
  readonly exploreVrBanner?: ExploreVrBannerData | null;
  /** Contentful `masterPlan` — same banner shape as VR. Omit hides the card. */
  readonly masterPlanBanner?: ExploreVrBannerData | null;
  readonly onMasterPlanBannerPress: () => void;
  readonly onExploreVrBannerPress: () => void;
}

function toGuestDetailsLayout(layout: AdhaListingLayout): GuestDetailsLayout {
  let paddingHorizontal = 32;
  if (layout.isPhone) {
    paddingHorizontal = 16;
  } else if (layout.isTablet) {
    paddingHorizontal = 8;
  }
  return {
    isPhone: layout.isPhone,
    isTablet: layout.isTablet,
    isCompact: layout.isCompact,
    carouselHeight: layout.carouselHeight,
    paddingHorizontal,
  };
}

/**
 * ADHA-owned project detail column — visual replica of guest project detail.
 * Lives in properties-web (does not edit guest-web).
 */
export function AdhaListingProjectPanel({
  layout,
  project,
  images,
  imagesLoading = false,
  onMapPress,
  onDocumentPress,
  downloadingDocumentId = null,
  exploreVrBanner = null,
  masterPlanBanner = null,
  onMasterPlanBannerPress,
  onExploreVrBannerPress,
}: Readonly<AdhaListingProjectPanelProps>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const labels = useGuestLabels();
  const guestLayout = useMemo(() => toGuestDetailsLayout(layout), [layout]);
  const typeScale = useMemo(
    () => getGuestDetailsTypeScale(guestLayout),
    [guestLayout],
  );
  const hasImages = images.length > 0;
  const showCarousel = hasImages || imagesLoading;
  const badgeSide = isRTL ? 'right' : 'left';

  return (
    <View style={styles.root}>
      {/*
        Same structure as guest `GuestUnitHero`:
        fixed-height frame + PlaceholderImage (#E3E3E3 + icon) filling it.
      */}
      <View
        style={[
          styles.hero,
          !hasImages
            ? {
                height: layout.carouselHeight,
                maxHeight: layout.carouselHeight,
                backgroundColor: colors.backgroundLevel2,
              }
            : {
                // Images: carousel sets height; still cap growth in ADHA split.
                maxHeight: layout.carouselHeight,
              },
        ]}
      >
        {showCarousel ? (
          <ImageCarousel
            images={[...images]}
            height={layout.carouselHeight}
            showNavigationButtons={!layout.isPhone}
            loading={imagesLoading}
          />
        ) : (
          <View
            style={[
              styles.heroPlaceholder,
              { backgroundColor: colors.backgroundLevel2 },
            ]}
          >
            <PlaceholderImage />
          </View>
        )}
        {project.statusBadge ? (
          <View
            style={[
              styles.constructionBadge,
              layout.isPhone && styles.constructionBadgeMobile,
              {
                [badgeSide]: layout.isPhone ? 12 : 16,
              },
            ]}
            pointerEvents="none"
          >
            <Typography
              variant="caption"
              color="primary"
              style={[
                styles.constructionBadgeText,
                layout.isPhone && styles.constructionBadgeTextMobile,
              ]}
            >
              {project.statusBadge}
            </Typography>
          </View>
        ) : null}
      </View>

      <View style={styles.header}>
        <View style={styles.titleRow}>
          <Typography
            variant={typeScale.pageTitle}
            color="primary"
            style={styles.titleText}
          >
            {project.title}
          </Typography>
          {project.latitude != null && project.longitude != null ? (
            <Pressable
              onPress={onMapPress}
              accessibilityRole="button"
              accessibilityLabel={project.location || 'Open map'}
            >
              <MapPinnedIcon
                size={typeScale.mapIconSize}
                color={colors.fontPrimary}
              />
            </Pressable>
          ) : null}
        </View>
        <View style={styles.locationDescription}>
          {project.location ? (
            <Typography
              variant={typeScale.location}
              color="secondary"
              style={[
                styles.locationText,
                { lineHeight: typeScale.locationLineHeight },
              ]}
            >
              {project.location}
            </Typography>
          ) : null}
          {project.description ? (
            <Typography
              variant={typeScale.description}
              color="secondary"
              style={[
                styles.descriptionText,
                { lineHeight: typeScale.descriptionLineHeight },
              ]}
            >
              {project.description}
            </Typography>
          ) : null}
        </View>
      </View>

      {masterPlanBanner ? (
        <ExploreVrBanner
          banner={masterPlanBanner}
          projectName={project.title}
          compact={layout.isPhone}
          onStartTourPress={onMasterPlanBannerPress}
        />
      ) : null}
      {exploreVrBanner ? (
        <ExploreVrBanner
          banner={exploreVrBanner}
          projectName={project.title}
          compact={layout.isPhone}
          onStartTourPress={onExploreVrBannerPress}
        />
      ) : null}

      <GuestUnitAboutAccordion
        unit={project}
        layout={guestLayout}
        typeScale={typeScale}
        labels={labels}
        hideUnitType
        defaultExpanded={false}
      />
      <UnitDocumentsAccordion
        documents={project.documents ?? []}
        onDocumentPress={onDocumentPress}
        downloadingDocumentId={downloadingDocumentId}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    gap: 16,
    minWidth: 0,
    maxWidth: '100%',
    width: '100%',
  },
  hero: {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    borderRadius: 16,
    overflow: 'hidden',
    // Keep ADHA split from letting PlaceholderImage flex:1 blow the column.
    flexGrow: 0,
    flexShrink: 0,
  },
  heroPlaceholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  constructionBadge: {
    position: 'absolute',
    top: 16,
    maxWidth: '85%',
    backgroundColor: '#E7E7E7',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    zIndex: 3,
  },
  constructionBadgeMobile: {
    top: 12,
    paddingHorizontal: 6,
  },
  constructionBadgeText: {
    fontFamily: 'Matter-SemiBold',
    lineHeight: 16,
    letterSpacing: 0,
  },
  constructionBadgeTextMobile: {
    lineHeight: 22,
  },
  header: {
    gap: 8,
    width: '100%',
    minWidth: 0,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
  },
  titleText: {
    letterSpacing: 0,
    flexShrink: 1,
  },
  locationDescription: {
    gap: 16,
    width: '100%',
    minWidth: 0,
  },
  locationText: {
    lineHeight: 18,
    letterSpacing: 0,
  },
  descriptionText: {
    lineHeight: 24,
    letterSpacing: 0,
  },
});

export default AdhaListingProjectPanel;
